import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { useAuthStore } from "@/stores/auth-store";

// ---------------------------------------------------------------------------
// /account (ALL-5481): destination of the account menu's "My Account" item.
// Read-only view of the signed-in session. This is the one place role names
// are shown; the account menu itself only carries name and email.
// ---------------------------------------------------------------------------

function roleLabel(roleName: string): string {
  const spaced = roleName.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export default function AccountPage() {
  const step = useAuthStore((s) => s.step);
  const user = useAuthStore((s) => s.user);
  const session = useAuthStore((s) => s.session);
  const organization = useAuthStore((s) => s.experience.activeOrganization);
  const roles = Array.isArray(session?.roleNames) ? session.roleNames.filter((r) => typeof r === "string" && r) : [];
  const hasIdentity = Boolean(user?.name || user?.email || roles.length > 0);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4" data-testid="account-page">
      <h1 className="text-xl font-extrabold text-ink">My Account</h1>
      {step === "loading" && (
        <p role="status" className="text-sm text-muted" data-testid="account-loading">
          Loading your account details...
        </p>
      )}
      {step === "error" && (
        <div
          role="alert"
          className="rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger"
          data-testid="account-error"
        >
          We couldn&apos;t load your account details. Check your connection and try again.
          {organization.supportReference ? ` Support reference: ${organization.supportReference}` : ""}
        </div>
      )}
      {step !== "loading" && step !== "error" && !hasIdentity && (
        <p className="text-sm text-muted" data-testid="account-empty">
          No signed-in account details are available yet. Sign in again to load your profile.
        </p>
      )}
      <Card>
        <CardBody className="flex flex-col gap-3">
          <CardTitle>Profile</CardTitle>
          <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Name</dt>
            <dd className="text-ink" data-testid="account-name">{user?.name ?? "—"}</dd>
            <dt className="text-muted">Email</dt>
            <dd className="text-ink" data-testid="account-email">{user?.email || "—"}</dd>
            <dt className="text-muted">Organization</dt>
            <dd className="text-ink">{organization.label}</dd>
            <dt className="text-muted">Roles</dt>
            <dd className="text-ink" data-testid="account-roles">
              {roles.length > 0 ? roles.map(roleLabel).join(", ") : "—"}
            </dd>
          </dl>
        </CardBody>
      </Card>
    </div>
  );
}
